import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiBriefcase, 
  FiUsers, 
  FiClipboard, 
  FiFileText, 
  FiTrendingUp, 
  FiTrendingDown,
  FiArrowRight,
  FiChevronLeft,
  FiChevronRight,
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
  FiMoreHorizontal,
  FiLoader
} from 'react-icons/fi';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import { dashboardApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useAccessCheck } from '../../hooks/useAccessCheck';

const Dashboard = () => {
  const navigate = useNavigate();
  const { role } = useAuth();
  const isAdmin = role === 'admin' || role === 'assessmentadmin';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useAccessCheck('cud_dashboard', '/requests');
  
  // API-driven data
  const [trustCenterStats, setTrustCenterStats] = useState(null);
  const [assessmentTrend, setAssessmentTrend] = useState([]);
  const [complianceData, setComplianceData] = useState([]);
  const [riskDistribution, setRiskDistribution] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [activityPage, setActivityPage] = useState(0);
  const activitiesPerPage = 5;


  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch core data; only fetch activity if admin
      const promises = [
        dashboardApi.getTrustCenterStats(),
        dashboardApi.getAssessmentTrends(),
        dashboardApi.getComplianceData(),
        dashboardApi.getRiskDistribution(),
      ];
      
      // Only fetch activity data for admin users
      if (isAdmin) {
        promises.push(dashboardApi.getActivity(10));
      }

      const results = await Promise.all(promises);
      
      setTrustCenterStats(results[0]);
      setAssessmentTrend(results[1]);
      setComplianceData(results[2]);
      setRiskDistribution(results[3]);
      
      if (isAdmin && results[4]) {
        setRecentActivities(Array.isArray(results[4]) ? results[4] : []);
      }
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Stats cards - dynamically built from API data
  const stats = trustCenterStats ? [
    { 
      title: 'Organizations', 
      value: trustCenterStats.totalOrganizations?.toLocaleString() || '0', 
      change: '+15%', 
      trend: 'up', 
      icon: FiClipboard,
      color: 'from-[#0077b5] to-[#0077b5]',
      bgColor: 'bg-[#0077b5]/10',
      textColor: 'text-blue-600'
    },
    { 
      title: 'Pending Assessments', 
      value: trustCenterStats.pendingAssessments?.toString() || '0', 
      change: '-5%', 
      trend: 'down', 
      icon: FiAlertCircle,
      color: 'from-[#F47F2E] to-[#F47F2E]',
      bgColor: 'bg-[#F47F2E]/10',
      textColor: 'text-amber-600'
    },
    { 
      title: 'Compliance Score', 
      value: `${trustCenterStats.complianceScore || 0}%`, 
      change: '+2.4%', 
      trend: 'up', 
      icon: FiCheckCircle,
      color: 'from-[#0077b5] to-[#0077b5]',
      bgColor: 'bg-[#0077b5]/10',
      textColor: 'text-violet-600'
    },
    { 
      title: 'Documents', 
      value: trustCenterStats.activeUsers?.toLocaleString() || '0', 
      change: '+8%', 
      trend: 'up', 
      icon: FiFileText,
      color: 'from-[#F47F2E] to-[#F47F2E]',
      bgColor: 'bg-[#0077b5]/10',
      textColor: 'text-emerald-600'
    },
  ] : [];

  const getActivityStatus = (activity) => {
    const actionText = (activity?.action || '').toLowerCase();
    const entityTypeText = (activity?.entityType || '').toLowerCase();

    if (actionText.includes('alert') || actionText.includes('failed') || actionText.includes('error')) {
      return 'error';
    }

    if (actionText.includes('pending') || actionText.includes('review')) {
      return 'warning';
    }

    if (actionText.includes('completed') || actionText.includes('added') || actionText.includes('onboarded')) {
      return 'success';
    }

    if (entityTypeText.includes('risk')) {
      return 'warning';
    }

    return 'info';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return <FiCheckCircle className="text-[#0077b5]" />;
      case 'warning': return <FiClock className="text-[#F47F2E]" />;
      case 'error': return <FiAlertCircle className="text-[#F47F2E]" />;
      default: return <FiClipboard className="text-[#0077b5]" />;
    }
  };



  if (loading) {
    const SkeletonItem = ({ className }) => (
      <div className={`bg-[#e6eef5] rounded-md ${className} animate-pulse`} />
    );

    return (
      <div className="space-y-6">
        {/* Header skeleton */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <SkeletonItem className="w-48 h-6 mb-2" />
            <SkeletonItem className="w-72 h-4" />
          </div>
        </div>

        {/* Stats Cards skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[0,1,2,3].map((i) => (
            <div key={i} className="rounded-xl p-6 shadow-sm border bg-white border-[#919191]/10">
              <div className="flex items-start justify-between">
                <SkeletonItem className="w-12 h-12 rounded-xl" />
                <SkeletonItem className="w-16 h-4 rounded" />
              </div>
              <div className="mt-4">
                <SkeletonItem className="w-24 h-8 rounded" />
                <div className="mt-2"><SkeletonItem className="w-32 h-3 rounded" /></div>
              </div>
            </div>
          ))}
        </div>

        {/* Detailed Main Charts & Workflow skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Assessment Trends (detailed) */}
          <div className="lg:col-span-8 rounded-xl p-6 shadow-sm border bg-white border-[#919191]/10">
            <div className="flex items-center justify-between mb-4">
              <div className="space-y-2">
                <SkeletonItem className="w-44 h-6" />
                <SkeletonItem className="w-72 h-3" />
              </div>
              <div className="flex items-center gap-2">
                <SkeletonItem className="w-12 h-4" />
                <SkeletonItem className="w-12 h-4" />
              </div>
            </div>
            <div className="mb-3 flex items-center gap-3">
              {/* legend placeholders */}
              <div className="flex items-center gap-2">
                <SkeletonItem className="w-3 h-3 rounded-full" />
                <SkeletonItem className="w-24 h-3" />
              </div>
              <div className="flex items-center gap-2">
                <SkeletonItem className="w-3 h-3 rounded-full" />
                <SkeletonItem className="w-24 h-3" />
              </div>
            </div>
            <div className="w-full h-64 rounded">
              <SkeletonItem className="w-full h-56 rounded" />
              <div className="mt-3 flex items-center justify-between">
                <SkeletonItem className="w-24 h-3" />
                <div className="flex gap-2">
                  <SkeletonItem className="w-12 h-3" />
                  <SkeletonItem className="w-12 h-3" />
                  <SkeletonItem className="w-12 h-3" />
                </div>
              </div>
            </div>
          </div>

          {/* Assessment Workflow (detailed) */}
          <div className="lg:col-span-4 rounded-xl p-6 shadow-sm border bg-white border-[#919191]/10">
            <div className="mb-4">
              <SkeletonItem className="w-40 h-6" />
              <SkeletonItem className="w-56 h-3 mt-2" />
            </div>
            <div className="space-y-4">
              {[1,2,3,4].map((s) => (
                <div key={s} className="flex items-center gap-4">
                  <SkeletonItem className="w-12 h-12 rounded-xl flex-shrink-0" />
                  <div className="flex-1">
                    <SkeletonItem className="w-40 h-4 mb-2" />
                    <SkeletonItem className="w-56 h-3" />
                    <div className="mt-2 flex gap-2">
                      <SkeletonItem className="w-20 h-3" />
                      <SkeletonItem className="w-16 h-3" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Risk Distribution & Compliance (detailed) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Risk Distribution detailed skeleton */}
          <div className="rounded-xl p-6 shadow-sm border bg-white border-[#919191]/10">
            <div className="flex items-center justify-between mb-4">
              <div>
                <SkeletonItem className="w-40 h-6" />
                <SkeletonItem className="w-56 h-3 mt-2" />
              </div>
            </div>
            <div className="space-y-3">
              {[0,1,2,3].map((i) => (
                <div key={i} className="flex items-center gap-4">
                  <SkeletonItem className="w-24 h-4" />
                  <SkeletonItem className="flex-1 h-4 rounded" />
                  <SkeletonItem className="w-12 h-4" />
                </div>
              ))}
            </div>
          </div>

          {/* Compliance Status detailed skeleton */}
          <div className="rounded-xl p-6 shadow-sm border bg-white border-[#919191]/10">
            <div className="flex items-center justify-between mb-4">
              <div>
                <SkeletonItem className="w-40 h-6" />
                <SkeletonItem className="w-56 h-3 mt-2" />
              </div>
              <SkeletonItem className="w-12 h-4" />
            </div>
            <div className="flex items-center gap-6">
              <div className="w-1/2">
                <SkeletonItem className="w-full h-40 rounded" />
              </div>
              <div className="w-1/2">
                <div className="space-y-3">
                  {[0,1,2].map((i) => (
                    <div key={i} className="flex items-center gap-3">
                      <SkeletonItem className="w-4 h-4 rounded-full" />
                      <SkeletonItem className="w-40 h-4" />
                      <SkeletonItem className="w-12 h-4" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity skeleton (admin area) */}
        {isAdmin && (
          <div className="rounded-xl p-6 shadow-sm border bg-white border-[#919191]/10">
            <div className="flex items-center justify-between mb-6">
              <SkeletonItem className="w-40 h-6" />
              <SkeletonItem className="w-20 h-6" />
            </div>
            <div className="space-y-4">
              {[0,1,2,3,4].map((i) => (
                <div key={i} className="flex items-start gap-4 p-3 rounded-lg">
                  <SkeletonItem className="w-10 h-10 rounded-full" />
                  <div className="flex-1">
                    <SkeletonItem className="w-56 h-4 mb-2" />
                    <SkeletonItem className="w-40 h-3" />
                  </div>
                  <SkeletonItem className="w-24 h-4" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className={`rounded-xl border p-4 bg-[#ffffff] border-[#F47F2E] text-[#F47F2E]`}>
        Error loading dashboard: {error}
        <button onClick={fetchDashboardData} className="ml-4 underline">Retry</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className={`mt-1 text-[#919191]`}>Welcome back! Here's what's happening with your trust center.</p>
        </div>
        
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className={`rounded-xl p-6 shadow-sm border hover:shadow-md transition-shadow bg-white border-[#919191]/30`}>
            <div className="flex items-start justify-between">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                <stat.icon className="text-white text-xl" />
              </div>
              <div className={`flex items-center gap-1 text-sm font-medium ${stat.trend === 'up' ? 'text-[#0077b5]' : 'text-[#F47F2E]'}`}>
                {stat.trend === 'up' ? <FiTrendingUp /> : <FiTrendingDown />}
                {stat.change}
              </div>
            </div>
            <div className="mt-4">
              <h3 className={`text-2xl font-bold text-[#53545a]`}>{stat.value}</h3>
              <p className={`text-sm mt-1 text-[#919191]`}>{stat.title}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Assessment Trends & Workflow Row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Assessment Trends Chart */}
        <div className={`lg:col-span-8 rounded-xl p-6 shadow-sm border bg-white border-[#919191]/30`}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className={`text-lg font-semibold text-[#53545a]`}>Assessment Trends</h2>
              <p className={`text-sm text-[#919191]`}>Completed vs Pending assessments</p>
            </div>
            <button className={`p-2 rounded-lg transition-colors hover:bg-[#919191]/10`}>
              <FiMoreHorizontal className="text-[#919191]" />
            </button>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={assessmentTrend}>
              <defs>
                <linearGradient id="colorCompleted" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0077b5" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#0077b5" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorPending" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F47F2E" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#F47F2E" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#919191" />
              <XAxis dataKey="month" stroke="#919191" fontSize={12} />
              <YAxis stroke="#919191" fontSize={12} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#ffffff', 
                  border: `1px solid #919191`, 
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                  color: '#53545a'
                }} 
              />
              <Area type="monotone" dataKey="completed" stroke="#0077b5" strokeWidth={2} fillOpacity={1} fill="url(#colorCompleted)" />
              <Area type="monotone" dataKey="pending" stroke="#F47F2E" strokeWidth={2} fillOpacity={1} fill="url(#colorPending)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Assessment Workflow */}
        <div className={`lg:col-span-4 rounded-xl p-6 shadow-sm border bg-white border-[#919191]/30`}>
          <div className="mb-6">
            <h2 className={`text-lg font-semibold text-[#53545a]`}>Assessment Workflow</h2>
            <p className={`text-sm text-[#919191]`}>Standard process flow</p>
          </div>
          <div className="space-y-4">
            {[
              { step: 1, title: 'Account Setup', desc: 'Admin creates assessments', icon: FiBriefcase, color: 'from-[#0077b5] to-[#0077b5]' },
              { step: 2, title: 'Generate Answers', desc: 'System/AI processes data', icon: FiClipboard, color: 'from-[#F47F2E] to-[#F47F2E]' },
              { step: 3, title: 'Review Process', desc: 'Reviewer approves/rejects', icon: FiCheckCircle, color: 'from-[#0077b5] to-[#0077b5]' },
              { step: 4, title: 'View Results', desc: 'User accesses final report', icon: FiUsers, color: 'from-[#F47F2E] to-[#F47F2E]' },
            ].map((item) => (
              <div key={item.step} className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center flex-shrink-0`}>
                  <item.icon className="text-white text-lg" />
                </div>
                <div className="flex-1">
                  <p className={`font-medium text-[#53545a]`}>{item.title}</p>
                  <p className={`text-sm text-[#919191]`}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Risk Distribution */}
        <div className={`rounded-xl p-6 shadow-sm border bg-white border-[#919191]/30`}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className={`text-lg font-semibold text-[#53545a]`}>Risk Distribution</h2>
              <p className={`text-sm text-[#919191]`}>By severity level</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={riskDistribution} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#919191" horizontal={false} />
              <XAxis type="number" stroke="#919191" fontSize={12} />
              <YAxis type="category" dataKey="category" stroke="#919191" fontSize={12} width={60} />
              <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: `1px solid #919191`, color: '#53545a' }} />
              <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                {riskDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Compliance Status Pie Chart */}
        <div className={`rounded-xl p-6 shadow-sm border bg-white border-[#919191]/30`}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className={`text-lg font-semibold text-[#53545a]`}>Compliance Status</h2>
              <p className={`text-sm text-[#919191]`}>Overall distribution</p>
            </div>
            <button className="p-2 hover:bg-[#919191]/10 rounded-lg transition-colors">
              <FiMoreHorizontal className="text-[#919191]" />
            </button>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={complianceData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {complianceData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-4 mt-4">
            {complianceData.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                <span className={`text-sm text-[#919191]`}>{item.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Activity - Admin Only */}
      {isAdmin && (
      <div className={`rounded-xl p-6 shadow-sm border bg-white border-[#919191]/30`}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className={`text-lg font-semibold text-[#53545a]`}>Recent Activity</h2>
            <p className={`text-sm text-[#919191]`}>Latest updates across the platform</p>
          </div>
          <button 
            onClick={() => navigate('/activity')}
            className="text-sm text-[#F47F2E] hover:text-[#F47F2E] font-medium flex items-center gap-1"
          >
            View All <FiArrowRight />
          </button>
        </div>
        <div className="space-y-4">
          {recentActivities.slice(activityPage * activitiesPerPage, (activityPage + 1) * activitiesPerPage).map((activity, index) => (
            <div 
              key={activity.id || `${activity.timestamp || index}`} 
              className={`flex items-start gap-4 p-3 rounded-lg transition-colors hover:bg-[#919191]/5 cursor-pointer`}
              onClick={() => navigate('/activity')}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-[#919191]/10`}>
                {getStatusIcon(getActivityStatus(activity))}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium text-[#53545a]`}>{activity.action}</p>
                <p className={`text-sm truncate text-[#919191]`}>{activity.entityName || '-'}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className={`text-sm text-[#919191]`}>{activity.userEmail || '-'}</p>
                <p className={`text-xs text-[#F47F2E]`}>{activity.userRole || '-'}</p>
                <p className={`text-xs text-[#919191]`}>{activity.timeAgo || '-'}</p>
              </div>
            </div>
          ))}
          {recentActivities.length === 0 && (
            <div className="p-3 text-sm text-[#919191]">No recent activity found.</div>
          )}
        </div>
        {recentActivities.length > activitiesPerPage && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#919191]/20">
            <span className="text-sm text-[#919191]">
              Showing {activityPage * activitiesPerPage + 1}–{Math.min((activityPage + 1) * activitiesPerPage, recentActivities.length)} of {recentActivities.length}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setActivityPage(p => p - 1)}
                disabled={activityPage === 0}
                className="p-1.5 rounded-lg transition-colors hover:bg-[#919191]/10 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <FiChevronLeft className="text-[#53545a]" />
              </button>
              <span className="text-sm font-medium text-[#53545a]">
                {activityPage + 1} / {Math.ceil(recentActivities.length / activitiesPerPage)}
              </span>
              <button
                onClick={() => setActivityPage(p => p + 1)}
                disabled={(activityPage + 1) * activitiesPerPage >= recentActivities.length}
                className="p-1.5 rounded-lg transition-colors hover:bg-[#919191]/10 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <FiChevronRight className="text-[#53545a]" />
              </button>
            </div>
          </div>
        )}
      </div>
      )}

    </div>
  );
};

export default Dashboard;